
import React, { useMemo } from 'react';
import {
  TechniqueFilters,
  Technique,
  Position,
  TechniquePosition,
  TechniqueType,
  TechniqueDifficulty
} from '@/types/technique';
import { TechniquesHeader } from '@/components/technique/TechniquesHeader';
import { TechniqueFilters as TechniqueFiltersComponent } from '@/components/technique/TechniqueFilters';
import { TechniqueList } from '@/components/technique/TechniqueList';
import { EmptyTechniques } from '@/components/technique/EmptyTechniques';
import { useTechniqueFilters } from '@/hooks/useTechniqueFilters';
import { useSavedTechniques } from '@/hooks/useSavedTechniques';
import { TechniqueLikesProvider } from '@/components/technique/TechniquesPage/TechniqueLikesProvider';
import { useTechniquesPerPage } from '@/hooks/useTechniquesPerPage';
import { useOfflineTechniques, useOfflinePositions } from '@/contexts/offline';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ConnectionQualityBanner } from '@/components/offline/OfflineBanner';

const Techniques = () => {
  const {
    filters,
    clearAllFilters,
    setSearchQuery,
    setSelectedPosition,
    setSelectedLevel,
    setSelectedType,
    setExcludeUserCreated
  } = useTechniqueFilters();
  const { itemsPerPage, setItemsPerPage } = useTechniquesPerPage();
  const { toast } = useToast();

  // Define help content for the techniques page
  const helpContent = {
    title: "Technique Library Help",
    description: "Learn how to make the most of the technique library",
    content: (
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-sm">Search & Filter</h4>
          <p className="text-sm text-muted-foreground">Use the search bar to find techniques by name or description. Filter by position, technique type, or difficulty level.</p>
        </div>
        <div>
          <h4 className="font-semibold text-sm">Save Techniques</h4>
          <p className="text-sm text-muted-foreground">Click the bookmark icon to save techniques to your personal collection in My Techniques.</p>
        </div>
        <div>
          <h4 className="font-semibold text-sm">Like Techniques</h4>
          <p className="text-sm text-muted-foreground">Use the heart icon to like techniques for quick reference and to help improve recommendations.</p>
        </div>
        <div>
          <h4 className="font-semibold text-sm">Request New Techniques</h4>
          <p className="text-sm text-muted-foreground">Don't see a technique you need? Use the "Request Technique" button to suggest additions to our library.</p>
        </div>
        <div>
          <h4 className="font-semibold text-sm">Technique Details</h4>
          <p className="text-sm text-muted-foreground">Click on any technique card to view detailed instructions, key points, and video demonstrations.</p>
        </div>
      </div>
    )
  };

  // Re-enabled offline-first hooks now that IndexedDB initialization is fixed
  const {
    data: techniquesData,
    loading: techniquesLoading,
    error: techniquesError,
    refresh: refreshTechniques
  } = useOfflineTechniques({ cacheFirst: true });

  const {
    data: positionsData,
    loading: positionsLoading,
    error: positionsError,
    refresh: refreshPositions
  } = useOfflinePositions({ cacheFirst: true });

  // Convert offline data to the format expected by the UI
  const techniques = useMemo((): Technique[] => {
    if (!techniquesData) return [];

    // Convert offline techniques to UI format and sort for stability
    return techniquesData
      .map(technique => {
        const position = positionsData?.find(pos => pos.id === technique.position_id);

        return {
          id: technique.id,
          name: technique.name,
          slug: technique.slug,
          description: technique.description,
          position_id: technique.position_id,
          position: position ? {
            id: position.id,
            name: position.name,
            description: position.description
          } as Position : undefined,
          technique_position: technique.technique_position as TechniquePosition,
          technique_type: technique.technique_type as TechniqueType,
          difficulty: technique.difficulty as TechniqueDifficulty,
          image_url: technique.image_url,
          video_url: technique.video_url,
          key_points: technique.key_points,
          common_mistakes: technique.common_mistakes,
          category: technique.category,
          subcategory: technique.subcategory,
          created_at: technique.created_at,
          updated_at: technique.updated_at,
          is_user_created: technique.is_user_created || false,
          created_by_user_id: technique.created_by_user_id,
          is_private: technique.is_private || false
        } as Technique;
      })
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [techniquesData, positionsData]);

  const positions = useMemo(() => {
    return positionsData || [];
  }, [positionsData]);

  const handleRefresh = async () => {
    try {
      await Promise.all([refreshTechniques(), refreshPositions()]);
      toast({
        title: 'Success',
        description: 'Techniques data refreshed successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to refresh techniques data. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Memoize filtered techniques based on stable dependencies
  const filteredTechniques = useMemo(() => {
    let filtered = [...techniques];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(technique =>
        technique.name.toLowerCase().includes(searchLower) ||
        technique.description.toLowerCase().includes(searchLower)
      );
    }

    if (filters.position) {
      filtered = filtered.filter(technique => technique.position?.id === filters.position);
    }

    if (filters.technique_position) {
      filtered = filtered.filter(technique => technique.technique_position === filters.technique_position);
    }

    if (filters.technique_type) {
      filtered = filtered.filter(technique => technique.technique_type === filters.technique_type);
    }

    if (filters.difficulty) {
      filtered = filtered.filter(technique => technique.difficulty === filters.difficulty);
    }

    if (filters.exclude_user_created) {
      filtered = filtered.filter(technique => !technique.is_user_created);
    }

    return filtered;
  }, [techniques, filters.search, filters.position, filters.technique_position, filters.technique_type, filters.difficulty, filters.exclude_user_created]);

  // Use stable techniques reference for saved techniques hook
  const { savedTechniques, handleSaveTechnique } = useSavedTechniques(techniques);

  // Create the filters object in the format expected by TechniqueFiltersComponent
  const techniqueFilters: TechniqueFilters = {
    search: filters.search,
    position: filters.position,
    technique_position: filters.technique_position,
    technique_type: filters.technique_type,
    difficulty: filters.difficulty,
    exclude_user_created: filters.exclude_user_created
  };

  // Create update function to convert from filters hook format to TechniqueFilters format
  const handleFilterUpdate = (newFilters: TechniqueFilters) => {
    // Update individual filter states using the hook's setters
    // Handle search - always update, even if empty string
    setSearchQuery(newFilters.search || '');
    if (newFilters.position !== undefined) {
      setSelectedPosition(newFilters.position);
    }
    if (newFilters.difficulty !== undefined) {
      setSelectedLevel(newFilters.difficulty);
    }
    if (newFilters.technique_type !== undefined) {
      setSelectedType(newFilters.technique_type);
    }
    if (newFilters.exclude_user_created !== undefined) {
      setExcludeUserCreated(newFilters.exclude_user_created);
    }
  };

  const handleClearFilters = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearAllFilters();
  };

  const isLoading = techniquesLoading || positionsLoading;
  const error = techniquesError || positionsError;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <TechniquesHeader helpContent={helpContent} />
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-gray-200 rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <TechniquesHeader helpContent={helpContent} />
        <div className="p-6 border border-destructive/30 rounded-lg bg-destructive/10 text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Techniques</h2>
          <p className="mb-4 text-muted-foreground">{error}</p>
          <Button onClick={handleRefresh} variant="outline">
            Retry Loading
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <TechniquesHeader helpContent={helpContent} />

      <ConnectionQualityBanner />

      <TechniqueFiltersComponent
        positions={positions}
        onFilterChange={handleFilterUpdate}
        currentFilters={techniqueFilters}
        onClearFilters={clearAllFilters}
      />

      <TechniqueLikesProvider
        techniques={techniques}
        onRefetch={handleRefresh}
      >
        {({ likedTechniqueIds, hiddenTechniqueIds, techniqueLikeCounts, handleLikeToggle: providerLikeToggle, handleHideToggle: providerHideToggle }) => (
          <>
            {filteredTechniques.length === 0 ? (
              <EmptyTechniques onClearFilters={handleClearFilters} />
            ) : (
              <TechniqueList
                techniques={filteredTechniques}
                onSaveTechnique={(techniqueId: string, e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const technique = techniques.find((t: { id: string }) => t.id === techniqueId);
                  if (technique) {
                    handleSaveTechnique(technique);
                  }
                }}
                savedTechniqueIds={savedTechniques}
                likedTechniqueIds={likedTechniqueIds}
                hiddenTechniqueIds={hiddenTechniqueIds}
                techniqueLikeCounts={techniqueLikeCounts}
                onLikeToggle={providerLikeToggle}
                onHideToggle={providerHideToggle}
                isLoading={isLoading}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        )}
      </TechniqueLikesProvider>
    </div>
  );
};

export default Techniques;
